import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { formatDateTime } from "@/lib/display-labels";
import type { LineManualEntry } from "@/features/lines/hooks";

const STATUS_LABEL: Record<string, string> = {
  COMPLETED: "Concluída",
  RUNNING: "Em processamento",
  FAILED: "Falha",
  PENDING: "Pendente",
};

interface Props {
  entry: LineManualEntry | null;
  open: boolean;
  onClose: () => void;
}

interface RowProps {
  label: string;
  value: React.ReactNode;
}

function Row({ label, value }: RowProps) {
  return (
    <div className="flex justify-between gap-2 px-2 py-1.5">
      <dt className="text-text-secondary">{label}</dt>
      <dd className="text-right font-mono text-text-primary">{value}</dd>
    </div>
  );
}

export function ManualEntryDetailDialog({
  entry,
  open,
  onClose,
}: Props): React.ReactElement | null {
  if (!entry) return null;
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] animate-fade-in bg-background/80" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[61] w-full max-w-md -translate-x-1/2 -translate-y-1/2 animate-scale-in rounded-lg border bg-card p-5 shadow-lg focus:outline-none">
          <div className="mb-3 flex items-start justify-between gap-2">
            <Dialog.Title className="text-base font-semibold text-text-primary">
              Entrada manual
            </Dialog.Title>
            <Dialog.Close className="rounded p-1 text-text-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]">
              <X className="h-4 w-4" />
              <span className="sr-only">Fechar</span>
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">
            Detalhes auditáveis da entrada manual selecionada.
          </Dialog.Description>

          <dl className="bg-bg-muted-rd/40 divide-y divide-border-subtle rounded border text-[12px]">
            <Row label="Status" value={STATUS_LABEL[entry.status] ?? entry.status} />
            <Row label="Origem" value={entry.origin} />
            <Row label="Linhas" value={entry.totalRows ?? "—"} />
            <Row label="Linhas válidas" value={entry.validRows ?? "—"} />
            <Row label="Autor" value={<span className="font-mono">{entry.createdBy}</span>} />
            <Row label="Criada em" value={formatDateTime(entry.createdAt)} />
            <Row
              label="Referência"
              value={entry.referenceAt ? formatDateTime(entry.referenceAt) : "—"}
            />
            <Row
              label="Concluída em"
              value={entry.completedAt ? formatDateTime(entry.completedAt) : "—"}
            />
            <Row label="ID" value={<span className="font-mono text-[11px]">{entry.id}</span>} />
          </dl>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
